// pdf-lib is dynamically imported when needed to reduce initial bundle size
import { saveAs } from 'file-saver';
import { toast } from '../../../common/toast';
import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { regenerateWorkups } from '../../../services/regenerateWorkupsService.js';
import {
  getWorkupReportLabel,
  getNegotiationReportTypeDisplayLabel,
  getWorkupS3FileName,
} from '../../../common/mappings.js';
import logger from '../../../utils/logger';

const normalizeToArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const collectParcelIds = ({ parcelIds, selectedRows, filteredData, getCompSheet }) => {
  if (parcelIds && normalizeToArray(parcelIds).length > 0) {
    return normalizeToArray(parcelIds).filter(Boolean);
  }

  if (selectedRows && selectedRows.length > 0) {
    return selectedRows.map((row) => row?.parcel_id).filter(Boolean);
  }

  if (getCompSheet && filteredData) {
    return filteredData
      .map((_, index) => getCompSheet?.[index]?.parcel_id?.[0])
      .filter(Boolean);
  }

  return [];
};

// Sanitize address for use in filename (remove invalid characters)
const sanitizeAddressForFilename = (address) => {
  if (!address) return '';
  return address
    .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
};

/**
 * Generates filename for workup download
 * @param {Array<string>} parcelIds - Array of parcel IDs
 * @param {Object} negotiationObj - Negotiation object
 * @param {string} reportType - 'standard' or 'assessment'
 * @param {string} typeOutput - 'fnmaSummary' or 'onepagermaps'
 * @returns {string} Generated filename
 */
export const generateWorkupFileName = (parcelIds, negotiationObj, reportType, typeOutput) => {
  const parcelCount = parcelIds?.length || 0;
  const address = parcelCount === 1 && negotiationObj?.cases
    ? negotiationObj.cases.find(c => c.parcel_id === parcelIds[0])?.Address || ''
    : '';
  
  const muniCode = negotiationObj?.MuniCode || negotiationObj?.muniCode || 'Workup';
  const namePart = parcelCount === 1
    ? sanitizeAddressForFilename(address)
    : (address ? sanitizeAddressForFilename(address) : muniCode);
  const reportTypeLabel = getNegotiationReportTypeDisplayLabel(reportType);
  const workupTypeLabel = getWorkupReportLabel(typeOutput);
  return `${namePart} ${reportTypeLabel} Comp ${workupTypeLabel}`;
};

/**
 * Fetches presigned URLs from backend API for given parcel IDs.
 * @param {Array<string>} parcelIds - Array of parcel IDs
 * @param {number} taxYear - Tax year
 * @param {string|Function} expectedFilename - One filename for every parcel, or (parcelId, s3Files) => filenames in merge order
 * @returns {Promise<Array<string|null>|Array<string[]>>} One URL per parcel, or URL lists when expectedFilename is a function
 */
export const fetchPresignedUrlsFromBackend = async (parcelIds, taxYear, expectedFilename) => {
  const perParcel = typeof expectedFilename === 'function';
  const empty = () => (perParcel ? [] : null);
  try {
    const response = await axiosInstance.post('/get_s3_files_batch', {
      parcel_ids: parcelIds,
      tax_year: taxYear,
      bucket_name: 'aventine-court-docs'
    });

    if (!response.data.success || !response.data.s3_files) {
      return parcelIds.map(empty);
    }

    return parcelIds.map(parcelId => {
      const s3Files = response.data.s3_files[parcelId] || [];
      const names = perParcel ? (expectedFilename(parcelId, s3Files) || []) : [expectedFilename];
      const urls = names.map((name) => {
        const matchingFile = s3Files.find((file) => (file?.Filename || file?.Key?.split('/').pop() || '') === name);
        if (!matchingFile) {
          logger.warn(`No matching file found for parcel ${parcelId} with expected filename "${name}"`);
        }
        return matchingFile?.Url || null;
      }).filter(Boolean);
      return perParcel ? urls : (urls[0] || null);
    });
  } catch (error) {
    logger.error('Error fetching presigned URLs from backend:', error);
    return parcelIds.map(empty);
  }
};

const fetchPdfBytes = async (url) => {
  const response = await fetch(url, { mode: 'cors' });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  return new Uint8Array(await response.arrayBuffer());
};

export const downloadIndividualPdfs = async (parcelUrlPairs, { zipFileName } = {}) => {
  if (!parcelUrlPairs?.length) {
    throw new Error('No workup files available to download.');
  }

  const jszipModule = await import('jszip');
  const JSZip = jszipModule.default ?? jszipModule;
  const zip = new JSZip();
  const failedParcelIds = [];

  logger.log('[downloadIndividualPdfs] Fetching PDFs for zip', { count: parcelUrlPairs.length });

  for (const { parcelId, url } of parcelUrlPairs) {
    try {
      const pdfBytes = await fetchPdfBytes(url);
      zip.file(`${parcelId}.pdf`, pdfBytes);
    } catch (error) {
      failedParcelIds.push(parcelId);
      logger.error(`Error fetching PDF for parcel ${parcelId}:`, error);
    }
  }

  const added = parcelUrlPairs.length - failedParcelIds.length;
  if (added === 0) {
    throw new Error('No workup PDFs could be fetched for download.');
  }

  logger.log('[downloadIndividualPdfs] Generating zip', { added, failed: failedParcelIds.length });

  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: { level: 3 },
  });

  const finalName = zipFileName?.trim?.() ? `${zipFileName.trim()}.zip` : 'Workups.zip';
  saveAs(zipBlob, finalName);

  if (failedParcelIds.length > 0) {
    toast.warning(
      `Downloaded ${added} of ${parcelUrlPairs.length} workups. ${failedParcelIds.length} failed to fetch.`
    );
  } else {
    toast.success(`Downloaded ${added} workup PDF${added === 1 ? '' : 's'}`);
  }

  return { added, failed: failedParcelIds.length, failedParcelIds };
};

export const mergeAndDownload = async (urls, { fileName }) => {
  // Dynamically import pdf-lib only when needed
  const { PDFDocument } = await import('pdf-lib');
  
  const mergedPdf = await PDFDocument.create();

  for (const url of urls) {
    try {
      const pdfBytes = await fetchPdfBytes(url);
      const pdf = await PDFDocument.load(pdfBytes);
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((page) => mergedPdf.addPage(page));
    } catch (error) {
      logger.error(`Error merging PDF from ${url}:`, error);
    }
  }

  const mergedPdfBytes = await mergedPdf.save();
  // fileName should always be provided by the caller now
  const finalName = fileName && fileName.trim().length > 0
    ? `${fileName}.pdf`
    : 'Workup.pdf';

  const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
  saveAs(blob, finalName);
};

export const downloadWorkupsFromS3 = async ({
  parcelIds = null,
  selectedRows = [],
  filteredData = null,
  getCompSheet = null,
  typeOutput = 'onepagermaps',
  combinePdfs = true,
  userId,
  onLoadingChange = null,
} = {}) => {
  try {
    onLoadingChange?.(true);
    const ids = collectParcelIds({ parcelIds, selectedRows, filteredData, getCompSheet });
    const uniqueIds = Array.from(new Set(ids));

    if (uniqueIds.length === 0) {
      toast.error('No valid parcel IDs found for download.');
      onLoadingChange?.(false);
      return;
    }

    // Map typeOutput to reportTypes array (same as toolbar)
    // const reportTypes = typeOutput === 'fnmaSummary' ? ['fnmaSummary'] : ['onepagermaps'];
    // If type output isnt in an array, convert it to an array
    let reportTypes = typeOutput;
    if (!Array.isArray(typeOutput)) {
      reportTypes = [typeOutput];
    }
    // Always regenerate workups first, then download (filename handled internally)
    await regenerateWorkups({
      parcelIds: uniqueIds,
      reportTypes,
      userId,
      download: true,
      combinePdfs,
    });
  } catch (error) {
    if (error?.name === 'AbortError') return;
    logger.error('Error generating and downloading workups:', error);
    throw error;
  } finally {
    onLoadingChange?.(false);
  }
};

// This function is for the combined non-regnerating workup flow. You have now created lots of stupid redundant shit, should refactor this file to tighten up.
export const downloadMergedWorkupsFromS3 = async ({
  filteredData = null,
  selectedRows = [],
  getCompSheet = null,
  negotiationObj = null,
  typeOutput = 'onepagermaps',
  onLoadingChange = null,
} = {}) => {
  try {
    onLoadingChange?.(true);
    const parcelIds = Array.from(
      new Set(collectParcelIds({ selectedRows, filteredData, getCompSheet })),
    );

    if (parcelIds.length === 0) {
      toast.error('No valid parcel IDs found for download.');
      return;
    }

    const taxYear = negotiationObj?.TaxYear
      ? parseInt(negotiationObj.TaxYear, 10)
      : new Date().getFullYear();
    const reportType =
      negotiationObj?.villageRun === 1
        ? 'village'
        : (negotiationObj?.reportType ?? 'standard');
    const expectedFilename = `${getWorkupS3FileName(reportType, typeOutput)}.pdf`;

    const response = await axiosInstance.post('/get_s3_files_batch', {
      parcel_ids: parcelIds,
      tax_year: taxYear,
      bucket_name: 'aventine-court-docs',
    });

    const s3FilesByParcel = response?.data?.success ? response.data.s3_files : {};
    const parcelUrlPairs = parcelIds
      .map((parcelId) => {
        const s3Files = s3FilesByParcel[parcelId] || [];
        // Exact match only — stem includes (e.g. sales_comps) would also hit sales_comps_nomaps.pdf
        const matchingFile = s3Files.find((file) => {
          const filename = file?.Filename || file?.Key?.split('/').pop() || '';
          return filename === expectedFilename;
        });
        return matchingFile?.Url ? { parcelId, url: matchingFile.Url } : null;
      })
      .filter(Boolean);

    if (parcelUrlPairs.length === 0) {
      toast.error(`No ${expectedFilename} workup files found to download.`);
      return;
    }

    const missingCount = parcelIds.length - parcelUrlPairs.length;
    if (missingCount > 0) {
      toast.warning(
        `${missingCount} of ${parcelIds.length} parcels have no ${expectedFilename} and will be skipped.`,
        { autoClose: 8000 },
      );
    }

    const fileName = generateWorkupFileName(parcelIds, negotiationObj, reportType, typeOutput);
    await mergeAndDownload(
      parcelUrlPairs.map(({ url }) => url),
      { fileName },
    );
    toast.success(`Downloaded merged workup PDF (${parcelUrlPairs.length} parcels)`);
  } catch (error) {
    logger.error('Error downloading merged workups from S3:', error);
    throw error;
  } finally {
    onLoadingChange?.(false);
  }
};
